import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Root as RadixDropdownMenu,
  Trigger,
  Content,
  Portal,
} from '@radix-ui/react-dropdown-menu'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { useIsMobile } from '@moonpig/web-shared-utils'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { Box } from '@moonpig/launchpad-components'
import { useAnalyticsHandlers, analytics } from '@moonpig/launchpad-analytics'
import { useOptionalSearchStore } from '../../../store/SearchStore'
import {
  filterCategoryEvent,
  milestoneResultsEvent,
} from '../../../pages/SearchPage/events'
import {
  useSearchAnalytics,
  sanitizeContentType,
} from '../../../pages/SearchPage/analytics'
import {
  ApplicableFilter,
  FilterCategory,
  IntegerFilter,
  FilterItem,
} from '../../../services/types/services'
import { DropDownButton } from '../DropDownButton'
import { getAllSelectedFilters } from '../../../utils/GalleryFiltering/getAllSelectedFilters'
import { createMenuFooter, createMenuItems } from './createMenu'
import { FilterSource } from '../../../types'
import { ResultsButton } from '../../FiltersMenu/components'
import { ClearButton } from './ClearButton'
import { useDrawer } from '../../../contexts/drawer'
import {
  IntegerFieldTypes,
  IntegerFilterInput,
  useIntegerInputFilter,
} from '../../IntegerFilterInput'
import { useFindLocaleText } from '../../../text-localisation'
import { StateIntegerFilter } from '../../../store/slices/filters/types'

const MENU_OFFSET_PX = '144px'
const MENU_MIN_WIDTH_PX = '360px'
const MENU_MAX_HEIGHT_PX = '480px'
const MENU_MIN_HEIGHT_PX = '136px'

const StyledDropdownContent = styled(Content)`
  position: relative;
  overflow-y: auto;
  overscroll-behavior: contain;
  max-height: min(calc(100vh - ${MENU_OFFSET_PX}), ${MENU_MAX_HEIGHT_PX});
  min-height: ${MENU_MIN_HEIGHT_PX};
  z-index: 1050;
  ${s({
    bgcolor: 'colorBackground01',
    minWidth: MENU_MIN_WIDTH_PX,
    boxShadow: 4,
    borderRadius: 4,
    borderColor: 'colorBorder03',
    border: 2,
  })};
`

type Props = PropsWithChildren<{
  filterCategory: FilterCategory | IntegerFilter
  selectedFilters: ApplicableFilter[]
  onChange: () => void
  toggleFilter: ({
    filter,
    select,
    source,
  }: {
    filter: ApplicableFilter
    select: boolean
    source: FilterSource
  }) => void
  onIntegerFilterChange: ({
    integerFilterId,
    value,
  }: {
    integerFilterId: string
    value: number | null
  }) => void
  integerFilters: StateIntegerFilter[]
  applyIntegerFilter: ({
    filter,
    filterId,
    value,
    source,
  }: {
    filter: IntegerFilter
    filterId: string
    value: number | null
    source: FilterSource
  }) => void
  clearFilters: ({
    source,
    category,
    type,
  }: {
    source: FilterSource
    category?: FilterCategory | IntegerFilter
    type?: 'filters' | 'sort'
  }) => void
  count: number
  loading: boolean
}>

export const DropDownMenu: FC<Props> = ({
  filterCategory,
  selectedFilters,
  onChange,
  toggleFilter,
  onIntegerFilterChange,
  integerFilters,
  applyIntegerFilter,
  clearFilters,
  count,
  loading,
}) => {
  const localiseText = useFindLocaleText()
  const [isOpen, setIsOpen] = useState(false)

  const isLaunchpadAnalytics = useSearchAnalytics()

  const pageType = useOptionalSearchStore(
    store => store.pageContext.type,
    'search',
  )

  const categoryAction = isOpen ? 'collapse' : 'expand'

  // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
  const categoryChangeEvent = filterCategoryEvent()
    .withEventData({
      category: `${pageType} action`.toLowerCase(),
      action: categoryAction,
      label: filterCategory.label.toLowerCase(),
    })
    .withContentData({
      content_type: sanitizeContentType(
        `${pageType} action | ${categoryAction} | ${filterCategory.label}`,
      ),
    })

  const isMobile = useIsMobile()
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)
  const viewResultsButtonRef = useRef<HTMLDivElement>(null)

  const useAgeSelectionUx =
    useExperiment('search-use-age-selection-ux-v2', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const isIntegerFilter =
    useAgeSelectionUx && filterCategory.__typename === 'IntegerFilter'

  const integerFilter = isIntegerFilter
    ? integerFilters.find(f => f.integerId === filterCategory.integerId)
    : undefined

  const integerFilterValue = integerFilter?.value || null

  const { createConfiguration, isIntegerFilterValueValid } =
    useIntegerInputFilter(integerFilter)

  const {
    show: showDrawer,
    update: updateDrawerContent,
    close: closeDrawer,
  } = useDrawer()

  const handleOnChange = () => {
    if (isLaunchpadAnalytics) analytics.send(categoryChangeEvent)
    setIsOpen(!isOpen)
    onChange()
  }

  const appliedCount = () => {
    if (filterCategory.__typename === 'IntegerFilter') {
      return (filterCategory as IntegerFilter).selectedValue !== null ? 1 : 0
    }
    return filterCategory.__typename === 'FilterCategory'
      ? getAllSelectedFilters(filterCategory.children).length
      : 0
  }

  const onFilterItemClick = useCallback(
    (filterItem: FilterItem) => {
      switch (filterItem.__typename) {
        case 'FilterCategory':
        case 'Filter':
          toggleFilter({
            filter: filterItem,
            select: !filterItem.isSelected,
            source: FilterSource.QuickFilters,
          })
          break
      }
    },
    [toggleFilter],
  )

  const milestoneLabel = integerFilterValue?.toString() || ''

  // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
  const milestoneSubmitEvent = milestoneResultsEvent()
    .withEventData({
      category: `${pageType} action`.toLowerCase(),
      label: milestoneLabel,
    })
    .withContentData({
      content_type:
        `${pageType} action | show milestone results | ${milestoneLabel}`
          .toLowerCase()
          .substring(0, 100),
    })

  const ageSubmitHandlers = useAnalyticsHandlers<HTMLFormElement>(
    isLaunchpadAnalytics && isIntegerFilterValueValid(integerFilterValue)
      ? { onSubmit: milestoneSubmitEvent }
      : {},
    {
      onSubmit: e => {
        e.preventDefault()
        if (isIntegerFilterValueValid(integerFilterValue)) {
          applyIntegerFilter({
            filter: {
              ...(filterCategory as IntegerFilter),
              selectedValue: integerFilterValue,
            },
            filterId: filterCategory.id,
            value: integerFilterValue,
            source: FilterSource.QuickFilters,
          })
        }
      },
    },
  )

  const dropDownMenuContent: (React.JSX.Element | null)[] = []

  if (isIntegerFilter) {
    dropDownMenuContent.push(
      <Box p={6} key="age-selection-filter-input">
        <form {...ageSubmitHandlers}>
          <IntegerFilterInput
            onChange={(ageInput: string) =>
              onIntegerFilterChange({
                integerFilterId: filterCategory.integerId,
                value: Number(ageInput) || null,
              })
            }
            input={createConfiguration(
              IntegerFieldTypes.age,
              integerFilterValue,
            )}
            hasValidValue={isIntegerFilterValueValid(integerFilterValue)}
          />
        </form>
      </Box>,
    )
  } else if (filterCategory.__typename === 'FilterCategory') {
    dropDownMenuContent.push(
      ...createMenuItems(
        (filterCategory as FilterCategory).children,
        toggleFilter,
        onFilterItemClick,
        isMobile,
      ),
    )
  }

  const dropDownMenuFooter = createMenuFooter({
    filterCategory,
    selectedFilters,
    integerFilters,
    isMobile,
    resultsButton: (
      <ResultsButton
        events={
          isLaunchpadAnalytics && isIntegerFilter
            ? { onClick: milestoneSubmitEvent }
            : undefined
        }
        compact={!isMobile}
        onClick={() => {
          if (isIntegerFilter) {
            applyIntegerFilter({
              filter: {
                ...(filterCategory as IntegerFilter),
                selectedValue:
                  integerFilterValue || /* istanbul ignore next */ null,
              },
              value: integerFilterValue,
              filterId: filterCategory.id,
              source: FilterSource.QuickFilters,
            })
          }
          handleOnChange()
          closeDrawer()
        }}
        loading={loading}
        resultsCount={count}
        label={
          isIntegerFilter ? localiseText('find.age_input.apply') : undefined
        }
        disabled={
          isIntegerFilter &&
          (integerFilterValue === null ||
            !isIntegerFilterValueValid(integerFilterValue))
        }
      />
    ),
    clearFilters,
    viewResultsButtonRef,
  })

  const dropDownMenuHeader = {
    left: 'close-button',
    right: (
      <ClearButton
        level={1}
        clearFilters={clearFilters}
        hide={appliedCount() === 0}
        category={filterCategory}
        source={FilterSource.QuickFilters}
      />
    ),
  }

  useEffect(() => {
    if (isOpen) {
      updateDrawerContent(
        dropDownMenuContent,
        dropDownMenuFooter,
        dropDownMenuHeader,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedFilters, count, loading, filterCategory, integerFilters])

  return isMobile ? (
    <DropDownButton
      label={filterCategory.label}
      isOpen={isOpen}
      count={appliedCount()}
      onClick={() => {
        showDrawer({
          title: filterCategory.label,
          triggerRef: dropdownButtonRef,
          header: dropDownMenuHeader,
          footer: dropDownMenuFooter,
          body: dropDownMenuContent,
          onClose: () => setIsOpen(false),
        })
        handleOnChange()
      }}
      ref={dropdownButtonRef}
    />
  ) : (
    <RadixDropdownMenu
      onOpenChange={handleOnChange}
      open={isOpen}
      modal={false}
    >
      <Trigger asChild>
        <DropDownButton
          label={filterCategory.label}
          isOpen={isOpen}
          count={appliedCount()}
          onClick={() => {}}
        />
      </Trigger>
      <Portal>
        <StyledDropdownContent
          sideOffset={8}
          align="start"
          side="bottom"
          avoidCollisions={false}
        >
          {dropDownMenuContent}
          {dropDownMenuFooter}
        </StyledDropdownContent>
      </Portal>
    </RadixDropdownMenu>
  )
}
